import React, { useCallback, useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  icons,
} from '@tapresearch/ui/components';

import {
  Campaign,
  TapQuestion,
  useLongitudinalQuestionResult,
  useRootTapAnswersList,
} from '../../../../api';
import {
  DotLoader,
  useFilter,
  QuestionResultWrapper,
  GraphToggleSwitch,
} from '../../../common';
import useAsset from '../../../hook/useAsset';
import { getChartWaveLabels } from '../components/charts/getChartWaveLabels';
import { useFormatResultsData } from '../hooks/useFormatResultsData';
import { ResponsiveLineChart } from './charts';
import { ResponsiveBumpChart } from './charts/ResponsiveBumpChart';
import { ResponsiveRatingLineChart } from './charts/ResponsiveRatingLineChart';
import { QuestionSingleWaveSelect } from './QuestionSingleWaveSelect';
import { SwitchQuestionGraph } from './SwitchQuestionGraph';
import { OpacityTransitionBox } from '../../../../../shared/OpacityTransitionBox';

interface LongitudinalQuestionProps {
  projectIdentifier: string;
  tapQuestion: TapQuestion['attributes'];
  waves: Campaign[];
}

export const LongitudinalQuestion = ({
  projectIdentifier,
  tapQuestion,
  waves,
}: LongitudinalQuestionProps) => {
  const { active_enum, constants } = useAsset();
  const isRanking =
    tapQuestion.tap_answer_type === constants.TAP_ANSWER_TYPE_RANKING;

  const tapSurveyIds = waves
    .filter((wave) => wave.attributes.status !== constants.STATUS_DRAFT)
    .map((wave) => wave.attributes.providable_id.toString());

  const { filterValues } = useFilter();
  const [groupRcAnswers, setGroupRcAnswers] = useState(false);
  const chartWaveLabels = getChartWaveLabels(waves);
  const [usePercentage, setUsePercentage] = useState(true);

  const { data: { attributes: result, meta: metaData } = {}, refetch } =
    useLongitudinalQuestionResult(
      tapQuestion.identifier,
      tapSurveyIds,
      filterValues as unknown as Record<string, string[]>,
      groupRcAnswers
    );

  const { answers_count: answersCount } = metaData || {};
  const { data: tapAnswers } = useRootTapAnswersList(tapQuestion.identifier);

  const formattedData =
    useFormatResultsData({
      result,
      tapAnswers,
      questionType: tapQuestion.tap_answer_type,
      chartWaveLabels,
      usePercentage: usePercentage && !isRanking,
    }) || [];

  const isUnchartable = [
    constants.TAP_ANSWER_TYPE_FREE_TEXT,
    constants.TAP_ANSWER_TYPE_MESSAGE,
  ].includes(tapQuestion.tap_answer_type);

  const questionType =
    active_enum.TapAnswerType[
      tapQuestion.tap_answer_type.toString() as keyof typeof active_enum.TapAnswerType
    ];

  const wrapperRef = useRef();
  const onChange = useCallback(() => {
    setGroupRcAnswers(!groupRcAnswers);
    refetch();
  }, [groupRcAnswers, refetch]);

  return (
    <QuestionResultWrapper
      tapQuestion={tapQuestion}
      isLongitudinalView
      headerRightContent={
        <Flex flexDirection="column">
          <Flex>
            <QuestionSingleWaveSelect
              waves={waves}
              projectIdentifier={projectIdentifier}
            />
          </Flex>

          {!isUnchartable && !isRanking && (
            <Flex justifyContent="right" mr={6} mt={2}>
              <GraphToggleSwitch
                {...{
                  leftItem: {
                    icon: icons.fi.Percent,
                    label: 'Graph by percentages',
                    callback: () => setUsePercentage(true),
                  },
                  rightItem: {
                    icon: icons.fi.Hash,
                    label: 'Graph by respondent totals',
                    callback: () => setUsePercentage(false),
                  },
                  selectedItem: usePercentage ? 'left' : 'right',
                }}
              />
            </Flex>
          )}
        </Flex>
      }
    >
      {!isUnchartable ? (
        <Flex
          data-testid="longitudinal-question-chart-wrapper"
          minH="400px"
          flexGrow={1}
          align="center"
          justify="center"
          ref={wrapperRef}
        >
          {!!formattedData.length ? (
            <OpacityTransitionBox w="100%" h="100%">
              <SwitchQuestionGraph
                match={constants.TAP_ANSWER_TYPE_SCALE_RANGE}
                component={ResponsiveRatingLineChart}
                legendDirection={!groupRcAnswers ? 'row' : 'column'}
                data={formattedData}
                waves={waves}
                answerType={tapQuestion.tap_answer_type}
                usePercentage={usePercentage}
                customLegendControlProps={{
                  isChecked: groupRcAnswers,
                  onChange: onChange,
                  answersCount: answersCount,
                }}
              />
              <SwitchQuestionGraph
                match={constants.TAP_ANSWER_TYPE_RANKING}
                component={ResponsiveBumpChart}
                data={formattedData}
                answerType={tapQuestion.tap_answer_type}
              />
              <SwitchQuestionGraph
                match={constants.TAP_ANSWER_TYPE_MULTIPLE_CHOICE}
                component={ResponsiveLineChart}
                data={formattedData}
                waves={waves}
                usePercentage={usePercentage}
                answerType={tapQuestion.tap_answer_type}
              />
              <SwitchQuestionGraph
                match={constants.TAP_ANSWER_TYPE_IMAGE_CHOICE}
                component={ResponsiveLineChart}
                data={formattedData}
                waves={waves}
                usePercentage={usePercentage}
                answerType={tapQuestion.tap_answer_type}
              />
            </OpacityTransitionBox>
          ) : (
            <DotLoader h={10} dotSize="20px" />
          )}
        </Flex>
      ) : (
        <Box mt={6} flexGrow={1} data-testid="unchartable-question-notice">
          <Alert
            rounded="none"
            bg="blue.50"
            py={3}
            opacity={1}
            variant="subtle"
            pl={8}
            status="info"
          >
            <AlertIcon opacity={0.5} color="blue.500" boxSize={4} />
            <AlertDescription
              color="gray.500"
              fontSize="xs"
              fontWeight="medium"
            >
              {questionType} type questions are not able to be graphed.
            </AlertDescription>
          </Alert>
        </Box>
      )}
    </QuestionResultWrapper>
  );
};
